import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { AssistantApiError, walterAssistantClient } from "@/services/walter-assistant";
import { BookOpen, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type PlaybookOption = { code: string; name: string };

export type PlaybookSwitcherProps = {
    currentPlaybookCode: string | null | undefined;
    currentPlaybookName: string | null | undefined;
    isInCall: boolean;
    onPlaybookSwitched?: () => void;
};

const PILL_CLASS = "flex w-fit items-center gap-1 rounded-full bg-gray-200 px-3 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-gray-700";

export function PlaybookSwitcher({ currentPlaybookCode, currentPlaybookName, isInCall, onPlaybookSwitched }: PlaybookSwitcherProps) {
    const [playbooks, setPlaybooks] = useState<PlaybookOption[]>([]);
    const [selected, setSelected] = useState<PlaybookOption | null>(null);
    const [isSwitching, setIsSwitching] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [fetchError, setFetchError] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [open, setOpen] = useState(false);
    const fetchedRef = useRef(false);

    useEffect(() => {
        if (!open || fetchedRef.current) return;
        fetchedRef.current = true;
        setIsLoading(true);
        setFetchError(false);
        walterAssistantClient
            .listPlaybooks()
            .then((data) => { setPlaybooks(data); setIsLoading(false); })
            .catch(() => { setFetchError(true); setIsLoading(false); });
    }, [open]);

    const handleClose = (v: boolean) => {
        setOpen(v);
        if (!v) {
            setSelected(null);
            setError(null);
            setIsSwitching(false);
            fetchedRef.current = false;
        }
    };

    const handleConfirm = async () => {
        if (!selected) return;
        setIsSwitching(true);
        setError(null);
        try {
            await walterAssistantClient.switchPlaybook(selected.code);
            onPlaybookSwitched?.();
            setOpen(false);
            setSelected(null);
        } catch (err) {
            if (err instanceof AssistantApiError) {
                setError(err.status === 404 ? "Aucun appel en cours ou playbook introuvable." : "Une erreur est survenue.");
            } else {
                setError("Une erreur est survenue.");
            }
        } finally {
            setIsSwitching(false);
        }
    };

    const isCurrentPlaybook = (pb: PlaybookOption) => !!currentPlaybookCode && pb.code === currentPlaybookCode;

    const pillLabel = currentPlaybookName ? `Playbook : ${currentPlaybookName}` : "Aucun playbook utilisé";

    if (!isInCall) {
        return <div className={PILL_CLASS}>{pillLabel}</div>;
    }

    return (
        <AlertDialog open={open} onOpenChange={handleClose}>
            <AlertDialogTrigger asChild>
                <button type="button" className={cn(PILL_CLASS, "cursor-pointer transition-colors hover:bg-gray-300")} aria-label="Changer de playbook">
                    <BookOpen className="size-2.5 shrink-0" aria-hidden />
                    {pillLabel}
                </button>
            </AlertDialogTrigger>
            <AlertDialogContent>
                <AlertDialogHeader>
                    <AlertDialogTitle>Changer de playbook</AlertDialogTitle>
                    <AlertDialogDescription>
                        Playbook actuel&nbsp;: <strong>{currentPlaybookName || "Inconnu"}</strong>.
                        La sélection d&apos;un nouveau playbook remet le coaching à zéro (phase, signaux, résumé).
                    </AlertDialogDescription>
                </AlertDialogHeader>

                <div className="flex flex-col gap-1.5 py-1">
                    {isLoading ? (
                        <p className="text-sm text-muted-foreground">Chargement…</p>
                    ) : fetchError ? (
                        <p className="text-sm text-red-600">Impossible de charger les playbooks.</p>
                    ) : playbooks.length === 0 ? (
                        <p className="text-sm text-muted-foreground">Aucun playbook disponible.</p>
                    ) : (
                        playbooks.map((pb) => (
                            <button
                                key={pb.code}
                                type="button"
                                onClick={() => setSelected(pb)}
                                className={cn(
                                    "rounded-md border px-3 py-2 text-left text-sm transition-colors",
                                    selected?.code === pb.code
                                        ? "border-primary bg-primary/10 font-medium text-primary"
                                        : "border-transparent hover:border-gray-200 hover:bg-gray-50"
                                )}
                            >
                                {pb.name}
                                {isCurrentPlaybook(pb) && (
                                    <span className="ml-2 text-xs text-muted-foreground">(actuel)</span>
                                )}
                            </button>
                        ))
                    )}
                </div>

                {error ? <p className="text-sm text-red-600">{error}</p> : null}

                <AlertDialogFooter>
                    <AlertDialogCancel disabled={isSwitching}>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                        onClick={(e) => { e.preventDefault(); void handleConfirm(); }}
                        disabled={!selected || isSwitching || (selected ? isCurrentPlaybook(selected) : false)}
                    >
                        {isSwitching ? <Loader2 className="mr-1.5 size-3.5 animate-spin" aria-hidden /> : null}
                        Changer de playbook
                    </AlertDialogAction>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}
